import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { ChatBubble, ChatInput, TypingIndicator, useTypingIndicator, playNotificationSound } from "@/components/shared/ChatComponents";

export default function ChatConversation() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [customer, setCustomer] = useState(null);
  const endRef = useRef(null);
  const prevCountRef = useRef(0);
  const isTyping = useTypingIndicator(api, customerId);

  const fetchMessages = () => {
    api.get(`/customers/${customerId}/chat`).then((r) => {
      const newMsgs = r.data;
      // Play sound if new customer message arrived
      const customerMsgs = newMsgs.filter((m) => m.sender === "customer").length;
      if (customerMsgs > prevCountRef.current && prevCountRef.current > 0) {
        playNotificationSound();
      }
      prevCountRef.current = customerMsgs;
      setMessages(newMsgs);
    }).catch(() => {});
  };

  useEffect(() => {
    api.get(`/customers/${customerId}`).then((r) => setCustomer(r.data)).catch(() => navigate("/crm/chat"));
    fetchMessages();
    api.post(`/chat/${customerId}/read`).catch(() => {});
    const iv = setInterval(() => { fetchMessages(); api.post(`/chat/${customerId}/read`).catch(() => {}); }, 3000);
    return () => clearInterval(iv);
  }, [customerId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, isTyping]);

  const handleSend = async (data) => {
    await api.post(`/customers/${customerId}/chat`, data);
    fetchMessages();
  };

  const grouped = {};
  messages.forEach((m) => {
    const date = new Date(m.created_at).toLocaleDateString("pt-BR");
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(m);
  });

  return (
    <div data-testid="chat-conversation" className="flex flex-col" style={{ height: "calc(100vh - 180px)" }}>
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => navigate("/crm/chat")} className="p-2 rounded-lg transition-colors" style={{ color: "var(--crm-text-muted)" }}>
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: "var(--crm-accent-subtle)", color: "var(--crm-accent)" }}>
          {customer?.name?.charAt(0)?.toUpperCase()}
        </div>
        <div>
          <p className="text-base font-bold" style={{ color: "var(--crm-text)" }}>{customer?.name || "..."}</p>
          <p className="text-xs" style={{ color: isTyping ? "var(--crm-accent)" : "var(--crm-text-muted)" }}>
            {isTyping ? "digitando..." : customer?.email}
          </p>
        </div>
      </div>

      <div className="crm-card flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-2">
          {messages.length === 0 && (
            <div className="text-center py-16" style={{ color: "var(--crm-text-dim)" }}>
              <p className="text-sm">Envie a primeira mensagem para iniciar o atendimento.</p>
            </div>
          )}
          {Object.entries(grouped).map(([date, msgs]) => (
            <div key={date}>
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px" style={{ background: "var(--crm-border)" }} />
                <span className="text-[10px] font-semibold px-2" style={{ color: "var(--crm-text-dim)" }}>{date}</span>
                <div className="flex-1 h-px" style={{ background: "var(--crm-border)" }} />
              </div>
              {msgs.map((m) => (
                <ChatBubble key={m.id} msg={m} isOwn={m.sender === "admin"} ownerLabel={m.sender === "admin" ? "Operador" : "Cliente"} />
              ))}
            </div>
          ))}
          {isTyping && <TypingIndicator label="Cliente" />}
          <div ref={endRef} />
        </div>

        <ChatInput onSend={handleSend} placeholder="Responder ao cliente..." apiInstance={api} customerId={customerId} />
      </div>
    </div>
  );
}
